/**
 * The Arty card's pinned actions: which ids exist and what a stored pin list may
 * hold. Framework-free so the api validates `User.pinnedActionIds` with the same
 * rules every app renders with. town-square's `catalog.ts` types every entry
 * against `ActionId`, and `api/test/town-square/arty-actions.spec.ts` asserts the
 * catalog and `ACTION_IDS` are identical, so neither can drift.
 */
export const ACTION_IDS = [
  'artyTake',
  'price',
  'collector',
  'galleries',
  'mockups',
  'artbook',
  'description',
  'title',
  'social',
  'share',
  'review',
  'critique',
  'rating',
  'pricing',
  'poll',
  'edit',
  'view',
] as const;

export type ActionId = (typeof ACTION_IDS)[number];

/** Library sparks are `spark:<lowercased trimmed CompletionType name>`. */
export const LIBRARY_SPARK_PREFIX = 'spark:';

/** Longest id we will store. A CompletionType name never approaches this. */
export const MAX_ACTION_ID_LENGTH = 128;

/** The card's shortcut buttons. Five is what fits a phone. */
export const MAX_PINS = 5;

/**
 * What an artist sees before customising: the five most-adopted actions per
 * user in a new signup's first 7 days (prod `sparks_clicked`, distinct users),
 * in that order.
 */
export const DEFAULT_PINS: readonly string[] = ['review', 'price', 'description', 'title', 'social'];

/** Raw entries accepted before dedupe, so a huge array is rejected on sight. */
const MAX_RAW_PINS = 20;

/**
 * The name half of a library spark id: already lowercased and trimmed, drawn
 * from a CompletionType name. Narrow on purpose - the id is a stored key.
 */
const LIBRARY_SPARK_NAME = /^[a-z0-9 _.'&-]+$/;

/**
 * The stored id for a library spark, from its CompletionType name. Folds accents and replaces
 * anything the id rule refuses ("Social Post (Custom)", "Show / Event Email") with "-", so every
 * spark an artist can see can also be pinned. A name already inside the rule keeps its id.
 */
export function librarySparkIdFor(name: string): string {
  const slug = name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9 _.'&-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[\s-]+|[\s-]+$/g, '');
  return `${LIBRARY_SPARK_PREFIX}${slug}`.slice(0, MAX_ACTION_ID_LENGTH).trimEnd();
}

export function isLibrarySparkId(id: string): boolean {
  if (!id.startsWith(LIBRARY_SPARK_PREFIX)) {
    return false;
  }
  if (id.length > MAX_ACTION_ID_LENGTH) {
    return false;
  }
  const name = id.slice(LIBRARY_SPARK_PREFIX.length);
  if (name.length === 0 || name !== name.trim()) {
    return false;
  }
  return LIBRARY_SPARK_NAME.test(name);
}

export function isKnownActionId(id: string): boolean {
  if ((ACTION_IDS as readonly string[]).includes(id)) {
    return true;
  }
  return isLibrarySparkId(id);
}

/**
 * Stored value semantics:
 *
 * - `null` - never customised. Show `DEFAULT_PINS`.
 * - `[]`   - deliberately zero shortcuts.
 * - anything else - exactly what the artist pinned, in their order.
 */
export function effectivePins(stored: readonly string[] | null | undefined): string[] {
  if (stored == null) {
    return [...DEFAULT_PINS];
  }
  return [...stored];
}

/** True once the stored list differs from the default sequence. */
export function isCustomPins(stored: readonly string[] | null | undefined): boolean {
  if (stored == null) {
    return false;
  }
  if (stored.length !== DEFAULT_PINS.length) {
    return true;
  }
  return stored.some((id, index) => {
    return id !== DEFAULT_PINS[index];
  });
}

/** Star toggle: unpin if present, append as the last shortcut if there is room. */
export function togglePin(current: readonly string[], id: string): string[] {
  if (current.includes(id)) {
    return current.filter((entry) => {
      return entry !== id;
    });
  }
  if (current.length >= MAX_PINS) {
    return [...current];
  }
  return [...current, id];
}

function isStorableEntry(entry: unknown): boolean {
  return typeof entry === 'string' && entry.length <= MAX_ACTION_ID_LENGTH;
}

/**
 * The cleaned list (trimmed, deduped, order kept), or `null` when the value is
 * not something we will keep. All-or-nothing: one unknown id, a non-string, or
 * more than `MAX_PINS` distinct ids rejects the whole list rather than silently
 * saving something the artist did not choose. Unknown ids pass only as library
 * sparks (`spark:<name>`): the curated set is closed, the spark library is not.
 */
export function normalizePins(input: unknown): string[] | null {
  if (!Array.isArray(input)) {
    return null;
  }
  if (input.length > MAX_RAW_PINS) {
    return null;
  }
  if (!input.every(isStorableEntry)) {
    return null;
  }
  const deduped = (input as string[]).reduce<string[]>((memo, entry) => {
    const id = entry.trim();
    if (!id || memo.includes(id)) {
      return memo;
    }
    return [...memo, id];
  }, []);
  if (deduped.length > MAX_PINS) {
    return null;
  }
  if (!deduped.every(isKnownActionId)) {
    return null;
  }
  return deduped;
}
